import { articlesData, getLocalizedArticlesData, SITE_URL, toolCategories } from '../data/contentData.js';
import { toolsData } from '../data/toolsData.js';

export const SUPPORTED_LANGUAGES = ['en', 'ar', 'fr', 'es', 'de', 'zh', 'pt'];

const languageLocaleMap = {
  en: 'en-US',
  ar: 'ar-SA',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE',
  zh: 'zh-CN',
  pt: 'pt-PT',
};

const staticMeta = {
  '/': {
    title: 'Free Online Tools, AI Tools Directory & SEO Resources | 3ToolsPro',
    description:
      '3ToolsPro offers free online tools for text, image, PDF, and calculators plus AI tools and multilingual SEO guides for faster workflows.',
  },
  '/tools': {
    title: 'Free Online Text, Image, PDF & Calculator Tools | 3ToolsPro',
    description:
      'Discover browser-based tools with clean URLs, fast performance, and practical workflows across text, image, PDF, and calculator categories.',
  },
  '/articles': {
    title: 'SEO Guides, Tool Tutorials & Productivity Articles | 3ToolsPro',
    description:
      'Read multilingual SEO-friendly articles with practical tutorials, tool walkthroughs, and internal links to related productivity utilities.',
  },
  '/blog': {
    title: 'Blog: SEO Guides, Tool Tutorials & AI Workflow Articles | 3ToolsPro',
    description:
      'Explore multilingual blog content featuring SEO strategies, tool tutorials, AI guides, and comparison articles for global ranking growth.',
  },
  '/ai-tools': {
    title: 'AI Tools Directory for Productivity and Marketing | 3ToolsPro',
    description:
      'Browse curated AI tools for content, design, development, and business workflows in a fast and indexable directory.',
  },
};

export const getCanonicalUrl = (pathname) => `${SITE_URL}${pathname === '/' ? '' : pathname}`;


const normalizePathname = (pathname) => {
  if (pathname === '/articles') return '/blog';
  if (pathname.startsWith('/articles/')) return pathname.replace('/articles/', '/blog/');
  return pathname;
};

const toSourceArticlePath = (pathname) => {
  if (pathname.startsWith('/blog/')) return pathname.replace('/blog/', '/articles/');
  if (pathname === '/blog') return '/articles';
  return pathname;
};

const getLangUrl = (pathname, language) => `${getCanonicalUrl(pathname)}?lang=${language}`;

const getAlternateLinks = (pathname) => [
  ...SUPPORTED_LANGUAGES.map((language) => ({
    hrefLang: language,
    href: getLangUrl(pathname, language),
  })),
  {
    hrefLang: 'x-default',
    href: getLangUrl(pathname, 'en'),
  },
];

const getWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '3ToolsPro',
  url: SITE_URL,
  inLanguage: SUPPORTED_LANGUAGES,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

const getToolSchema = (tool, pathname, language) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: tool.name,
  description: tool.description,
  applicationCategory: tool.category,
  operatingSystem: 'Web',
  inLanguage: language,
  url: getCanonicalUrl(pathname),
  isAccessibleForFree: true,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
});

const getArticleSchema = (article, pathname, language) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  datePublished: article.publishedAt,
  dateModified: article.publishedAt,
  inLanguage: language,
  mainEntityOfPage: getCanonicalUrl(pathname),
  author: {
    '@type': 'Organization',
    name: '3ToolsPro',
  },
  publisher: {
    '@type': 'Organization',
    name: '3ToolsPro',
  },
  keywords: (article.keywords || []).join(', '),
});

const getToolFaqSchema = (tool) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: `Is ${tool.name} free to use?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Yes, ${tool.name} is free and works directly in your browser.`,
      },
    },
    {
      '@type': 'Question',
      name: `How do I use ${tool.name}?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Open the ${tool.name} page, provide the required input, and copy or download your result.`,
      },
    },
  ],
});

const getToolHowToSchema = (tool, pathname) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: `How to use ${tool.name}`,
  description: `Step-by-step usage guide for ${tool.name}.`,
  totalTime: 'PT2M',
  step: [
    {
      '@type': 'HowToStep',
      name: 'Open tool',
      text: `Navigate to ${getCanonicalUrl(pathname)}.`,
    },
    {
      '@type': 'HowToStep',
      name: 'Add input',
      text: `Enter or upload the required input for ${tool.name}.`,
    },
    {
      '@type': 'HowToStep',
      name: 'Generate result',
      text: 'Run the tool and review your output.',
    },
    {
      '@type': 'HowToStep',
      name: 'Use output',
      text: 'Copy, download, or reuse the generated result.',
    },
  ],
});

export const getMetaForPath = (pathname, language = 'en') => {
  const normalizedLanguage = SUPPORTED_LANGUAGES.includes(language) ? language : 'en';
  const locale = languageLocaleMap[normalizedLanguage] || 'en-US';
  const normalizedPathname = normalizePathname(pathname);
  const sourcePathname = toSourceArticlePath(normalizedPathname);

  if (normalizedPathname === '/search' || normalizedPathname === '/__admin-config' || normalizedPathname === '/translation-dashboard') {
    return {
      title: 'Private Utility Route | 3ToolsPro',
      description: 'This route is intentionally excluded from search indexing.',
      canonicalUrl: getCanonicalUrl(normalizedPathname),
      robots: 'noindex, nofollow',
      language: normalizedLanguage,
      alternates: getAlternateLinks(normalizedPathname),
    };
  }

  const tool = toolsData.find((item) => item.path === normalizedPathname);
  if (tool) {
    return {
      title: `${tool.name} Online Tool - Free ${tool.category} Utility | 3ToolsPro`,
      description: `${tool.description}. Learn what this tool does, how to use it, key benefits, FAQs, and related tools.`,
      canonicalUrl: getCanonicalUrl(normalizedPathname),
      language: normalizedLanguage,
      alternates: getAlternateLinks(normalizedPathname),
      openGraph: {
        locale,
        siteName: '3ToolsPro',
      },
      schema: [
        getWebSiteSchema(),
        getToolSchema(tool, normalizedPathname, normalizedLanguage),
        getToolFaqSchema(tool),
        getToolHowToSchema(tool, normalizedPathname),
        getBreadcrumbSchema([
          { name: 'Home', url: getCanonicalUrl('/') },
          { name: 'Tools', url: getCanonicalUrl('/tools') },
          { name: tool.name, url: getCanonicalUrl(normalizedPathname) },
        ]),
      ],
    };
  }

  const category = toolCategories.find((item) => `/tool-category/${item.slug}` === normalizedPathname);
  if (category) {
    return {
      title: `${category.name} Tools Collection - Free Online Utilities | 3ToolsPro`,
      description: category.description,
      canonicalUrl: getCanonicalUrl(normalizedPathname),
      language: normalizedLanguage,
      alternates: getAlternateLinks(normalizedPathname),
      openGraph: {
        locale,
        siteName: '3ToolsPro',
      },
      schema: [
        getWebSiteSchema(),
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: category.name,
          description: category.description,
          url: getCanonicalUrl(normalizedPathname),
          inLanguage: normalizedLanguage,
        },
        getBreadcrumbSchema([
          { name: 'Home', url: getCanonicalUrl('/') },
          { name: 'Tool Categories', url: getCanonicalUrl('/tools') },
          { name: category.name, url: getCanonicalUrl(normalizedPathname) },
        ]),
      ],
    };
  }

  const localizedArticle = getLocalizedArticlesData(normalizedLanguage).find((item) => `/articles/${item.slug}` === sourcePathname);
  const defaultArticle = articlesData.find((item) => `/articles/${item.slug}` === sourcePathname);
  const article = localizedArticle || defaultArticle;

  if (article) {
    return {
      title: `${article.title} | SEO Guide & Tutorial | 3ToolsPro`,
      description: article.description,
      canonicalUrl: getCanonicalUrl(normalizedPathname),
      language: normalizedLanguage,
      alternates: getAlternateLinks(normalizedPathname),
      openGraph: {
        type: 'article',
        locale,
        siteName: '3ToolsPro',
      },
      schema: [
        getWebSiteSchema(),
        getArticleSchema(article, normalizedPathname, normalizedLanguage),
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (article.sections?.faqs || []).map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
        getBreadcrumbSchema([
          { name: 'Home', url: getCanonicalUrl('/') },
          { name: 'Articles', url: getCanonicalUrl('/articles') },
          { name: article.title, url: getCanonicalUrl(normalizedPathname) },
        ]),
      ],
    };
  }

  const fallback = staticMeta[normalizedPathname] || {
    title: 'Free Online Tools for SEO, Text, Image, and PDF Workflows | 3ToolsPro',
    description: 'Use browser-based online tools with strong technical SEO, multilingual support, and fast user experience.',
  };

  return {
    ...fallback,
    canonicalUrl: getCanonicalUrl(normalizedPathname),
    language: normalizedLanguage,
    alternates: getAlternateLinks(normalizedPathname),
    openGraph: {
      locale,
      siteName: '3ToolsPro',
    },
    schema: [
      getWebSiteSchema(),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: fallback.title,
        description: fallback.description,
        url: getCanonicalUrl(normalizedPathname),
        inLanguage: normalizedLanguage,
      },
      getBreadcrumbSchema([
        { name: 'Home', url: getCanonicalUrl('/') },
        { name: fallback.title, url: getCanonicalUrl(normalizedPathname) },
      ]),
    ],
  };
};
